import {
  ChatBubbleOutline,
  InfoOutlined,
  InsightsOutlined,
  Inventory2Outlined,
} from '@mui/icons-material';
import {
  Box,
  capitalize,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Modal from '@mui/material/Modal';
import React, { useState } from 'react';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { generateUniqueKey } from '../../../helpers/app';
import { languageLabel } from '../../../helpers/chatDashboardHelper';
import messageSanitizeSchema from '../../../helpers/messageSanitizeSchema';
import { useTranslation } from '../../../hooks/useTranslation';
import { Conversation } from '../../../interfaces/Analytics';
import Button from '../Button/Button';
import StyledBaseModal from './StyledBaseModal';
import StyledConversationMessages from './StyledConversationMessages';

type ConversationDetailsModalProps = {
  item: Conversation;
};

const ConversationDetailsModal: React.FC<ConversationDetailsModalProps> = ({
  item,
}) => {
  const { t } = useTranslation();
  const language = languageLabel(item.conversation_language, t);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const actionIconButtonStyle = {
    minWidth: '44px',
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    background: '#F1F5F9',
    borderColor: 'rgba(15, 23, 42, 0.06)',
    color: '#1841ff',
    padding: '0.5rem',
  };
  const closeButtonStyle = {
    minWidth: '110px',
    height: '42px',
    borderRadius: '10px',
    color: '#0F172A',
    borderColor: 'rgba(15, 23, 42, 0.28)',
    padding: '0.5rem 1.1rem',
  };
  const dataCollected = Object.keys(item.data_collected ?? {}).map(key => ({
    key,
    value: item.data_collected[key],
  }));
  const visibleMessages = item.messages.filter(
    msg => msg.role !== 'tool' && !msg.tool_calls
  );

  return (
    <>
      <Tooltip
        title={t('View the conversation details')}
        placement="right-start"
        arrow
      >
        <span>
          <Button
            color="inherit"
            onClick={handleOpen}
            padding="0.5rem"
            size="small"
            variant="outlined"
            style={actionIconButtonStyle}
          >
            <InfoOutlined />
          </Button>
        </span>
      </Tooltip>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <StyledBaseModal id="modal-box" sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.75,
                bgcolor: '#EEF2FF',
                color: '#1841ff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <InsightsOutlined fontSize="small" />
            </Box>
            <Typography
              id="modal-modal-title"
              variant="h6"
              component="h2"
              sx={{ fontWeight: 700, color: '#0F172A' }}
            >
              {t('Conversation Details')}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: 'rgba(15, 23, 42, 0.08)' }} />

          <Box
            sx={{
              border: '1px solid rgba(15, 23, 42, 0.08)',
              bgcolor: '#F8FAFC',
              borderRadius: 2.5,
              p: 2,
            }}
          >
            <Typography
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.9,
                fontWeight: 700,
                fontSize: '1rem',
                color: '#0F172A',
                mb: 1,
              }}
            >
              <InsightsOutlined sx={{ fontSize: '1.1rem', color: '#1841ff' }} />
              {t('Conversation Summary')}
            </Typography>
            <Typography
              id="modal-modal-description"
              sx={{
                color: '#334155',
                lineHeight: 1.7,
                fontSize: '0.98rem',
              }}
            >
              {item.conversation_summary || '--'}
            </Typography>
            {language && (
              <Typography sx={{ mt: 1, color: '#64748B', fontSize: '0.9rem' }}>
                {t('Language')}: {language}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              border: '1px solid rgba(15, 23, 42, 0.08)',
              borderRadius: 2.5,
              p: 2,
              bgcolor: '#FFFFFF',
            }}
          >
            <Typography
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.9,
                fontWeight: 700,
                fontSize: '1rem',
                color: '#0F172A',
                mb: 1.5,
              }}
            >
              <Inventory2Outlined
                sx={{ fontSize: '1.1rem', color: '#1841ff' }}
              />
              {t('Data Collected')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1,
              }}
            >
              {dataCollected.length ? (
                dataCollected.map(({ key, value }) => (
                  <Box
                    key={generateUniqueKey()}
                    sx={{
                      px: 1.25,
                      py: 1,
                      borderRadius: 1.5,
                      bgcolor: '#F8FAFC',
                      border: '1px solid rgba(15, 23, 42, 0.06)',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.93rem', color: '#0F172A' }}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          color: '#64748B',
                        }}
                      >
                        {capitalize(key)}:{' '}
                      </Typography>
                      {value ? String(value) : '--'}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={{ color: '#64748B', fontSize: '0.92rem' }}>
                  --
                </Typography>
              )}
            </Box>
          </Box>

          <StyledConversationMessages>
            <Typography
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.9,
                fontWeight: 700,
                fontSize: '1rem',
                color: '#0F172A',
              }}
            >
              <ChatBubbleOutline
                sx={{ fontSize: '1.1rem', color: '#1841ff' }}
              />
              {t('Conversation Messages')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.1,
                maxHeight: 380,
                overflow: 'auto',
                pr: 0.5,
              }}
            >
              {visibleMessages.map((msg, idx) => {
                const isUser = msg.role?.toLowerCase() === 'user';
                return (
                  <Box
                    key={`${msg.role}-${idx}`}
                    sx={{
                      display: 'flex',
                      justifyContent: isUser ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: { xs: '92%', sm: '78%' },
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                        border: '1px solid rgba(15, 23, 42, 0.08)',
                        bgcolor: isUser ? '#1841ff' : '#F8FAFC',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          color: isUser ? '#DBEAFE' : '#64748B',
                          mb: 0.4,
                        }}
                      >
                        {isUser ? t('User') : t('Assistant')}
                      </Typography>
                      <Box
                        sx={{
                          fontSize: '0.95rem',
                          lineHeight: 1.55,
                          color: isUser ? '#FFFFFF' : '#0F172A',
                          '& p': { margin: 0 },
                          '& p + p': { mt: 0.75 },
                          '& a': {
                            color: isUser ? '#DBEAFE' : '#1841ff',
                          },
                        }}
                      >
                        <Markdown
                          rehypePlugins={[
                            rehypeRaw,
                            [rehypeSanitize, messageSanitizeSchema],
                          ]}
                        >
                          {msg.content}
                        </Markdown>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </StyledConversationMessages>

          <Stack
            direction="row"
            gap="1rem"
            justifyContent="flex-end"
            marginTop={{ xs: 0, sm: 0.5 }}
          >
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              padding="0.5rem 1.2rem"
              onClick={handleClose}
              style={closeButtonStyle}
            >
              {t('Close')}
            </Button>
          </Stack>
        </StyledBaseModal>
      </Modal>
    </>
  );
};

export default ConversationDetailsModal;
